import {
  MarkerType,
  type Connection,
  type Edge,
  type EdgeChange,
  type EdgeMarker,
  type Node,
  type OnSelectionChangeParams,
  type XYPosition,
} from "reactflow";
import type AddNodeData from "./AddNodeData";
import type CoreGraphAdapter from "./CoreGraphAdapter";
import type ExecutionStepPhase from "./ExecutionStepPhase";
import type LabelType from "./MathLabelPartType";
import type NodeData from "./NodeData";
import {
  idleWalkthroughState,
  type default as NodeWalkthroughState,
} from "./NodeWalkthroughState";
import { formatNumber, type DecimalPlaces } from "./NumberFormatter";
import type OutputItemType from "./OutputItemType";
import { randomInteger } from "./RandomUtilities";

const addReactFlowNode = (
  addNodeData: AddNodeData,
  position: XYPosition,
  nodes: Node[],
): Node[] => {
  const node: Node = {
    id: addNodeData.nodeId,
    type: "custom", // registered in Graph
    data: buildReactFlowNodeData(addNodeData),
    dragHandle: ".drag-handle", // corresponds to className in NoteTitle
    selected: true,
    position,
  };
  return nodes.concat(node);
};

const getNewReactFlowNodePosition = (
  nodes: Node[],
  lastSelectedNodeId: string | null,
): XYPosition => {
  const randomMin = -50;
  const randomMax = 50;
  let referenceNode = findLastSelectedNode(nodes, lastSelectedNodeId);

  // Pick a random node. For this case, pick any node that has id since the
  // array may contain undefined value
  if (referenceNode === null) {
    referenceNode = nodes.find((node) => "id" in node) ?? null;
  }

  if (referenceNode !== null) {
    return {
      x: referenceNode.position.x + randomInteger(randomMin, randomMax),
      y: referenceNode.position.y + randomInteger(randomMin, randomMax),
    };
  } else {
    return {
      x: randomInteger(0, randomMax),
      y: randomInteger(0, randomMax),
    };
  }
};

const updateReactFlowNodeName = (
  nodeId: string,
  name: string,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      const data = node.data as NodeData;
      data.name = name;
      // Set the new data to notify React Flow about the change
      const newData: NodeData = { ...node.data };
      node.data = newData;
    }

    return node;
  });
};

const updateReactFlowNodeInputValue = (
  nodeId: string,
  inputPortId: string,
  value: string,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      const data = node.data as NodeData;
      const inputItem = data.inputItems.find(
        (inputItem) => inputItem.id === inputPortId,
      );
      if (inputItem === undefined) {
        throw new Error(`Should find input port ${inputPortId}`);
      }
      inputItem.value = value;
      // Set the new data to notify React Flow about the change
      const newData: NodeData = { ...node.data };
      node.data = newData;
    }

    return node;
  });
};

const hideInputField = (connection: Connection, nodes: Node[]): Node[] => {
  const targetHandle = connection.targetHandle;
  if (targetHandle === null) {
    throw new Error("Should have target handle");
  }

  return nodes.map((node) => {
    if (node.id === connection.target) {
      const data = node.data as NodeData;
      const inputItem = data.inputItems.find(
        (inputItem) => inputItem.id === connection.targetHandle,
      );
      if (inputItem === undefined) {
        throw new Error(`Should find input port ${targetHandle}`);
      }
      inputItem.showInputField = false;
      // Set the new data to notify React Flow about the change
      const newData: NodeData = { ...node.data };
      node.data = newData;
    }

    return node;
  });
};

const showInputFields = (edges: Edge[], nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    edges.forEach((edge) => {
      if (typeof edge.targetHandle !== "string") {
        return;
      }
      if (edge.target !== node.id) {
        return;
      }

      const data = node.data as NodeData;
      const inputItem = data.inputItems.find(
        (inputItem) => inputItem.id === edge.targetHandle,
      );
      if (inputItem === undefined) {
        throw new Error(`Should find input port ${edge.targetHandle}`);
      }
      inputItem.showInputField = true;
      // Set the new data to notify React Flow about the change
      const newData: NodeData = { ...node.data };
      node.data = newData;
    });

    return node;
  });
};

const findRemovedEdges = (changes: EdgeChange[], edges: Edge[]): Edge[] => {
  const removedEdgeIds = new Set<string>();
  changes.forEach((change) => {
    switch (change.type) {
      case "remove": {
        removedEdgeIds.add(change.id);
        break;
      }
    }
  });

  const removedEdges: Edge[] = [];
  edges.forEach((edge) => {
    if (removedEdgeIds.has(edge.id)) {
      removedEdges.push(edge);
    }
  });
  return removedEdges;
};

const incomingInfluenceColor = "#2196f3";
const outgoingInfluenceColor = "#ff9800";

const findInfluenceEdgeIds = (
  selectedNodeIds: string[],
  edges: Edge[],
  isGlobal: boolean,
): { incomingEdgeIds: Set<string>; outgoingEdgeIds: Set<string> } => {
  const incomingEdgeIds = new Set<string>();
  const outgoingEdgeIds = new Set<string>();
  const selectedSet = new Set(selectedNodeIds);

  if (!isGlobal) {
    edges.forEach((edge) => {
      if (selectedSet.has(edge.target)) {
        incomingEdgeIds.add(edge.id);
      }
      if (selectedSet.has(edge.source)) {
        outgoingEdgeIds.add(edge.id);
      }
    });
    return { incomingEdgeIds, outgoingEdgeIds };
  }

  // Global incoming (ancestors)
  const incomingQueue = [...selectedNodeIds];
  const incomingVisitedNodes = new Set<string>(selectedNodeIds);
  while (incomingQueue.length > 0) {
    const currentNodeId = incomingQueue.shift();
    if (currentNodeId === undefined) {
      break;
    }
    edges.forEach((edge) => {
      if (edge.target === currentNodeId) {
        incomingEdgeIds.add(edge.id);
        if (!incomingVisitedNodes.has(edge.source)) {
          incomingVisitedNodes.add(edge.source);
          incomingQueue.push(edge.source);
        }
      }
    });
  }

  // Global outgoing (descendants)
  const outgoingQueue = [...selectedNodeIds];
  const outgoingVisitedNodes = new Set<string>(selectedNodeIds);
  while (outgoingQueue.length > 0) {
    const currentNodeId = outgoingQueue.shift();
    if (currentNodeId === undefined) {
      break;
    }
    edges.forEach((edge) => {
      if (edge.source === currentNodeId) {
        outgoingEdgeIds.add(edge.id);
        if (!outgoingVisitedNodes.has(edge.target)) {
          outgoingVisitedNodes.add(edge.target);
          outgoingQueue.push(edge.target);
        }
      }
    });
  }

  return { incomingEdgeIds, outgoingEdgeIds };
};

/**
 * @param isExplaining Whether the inspector is spelling out the working of the
 * selected nodes. Only then is there anything for the animation to illustrate.
 * @param derivativePathNodeIds Node IDs the derivative propagates through. An
 * edge whose far end is outside this set carries no gradient, so it stays
 * still even when it touches a selected node.
 * @param isGlobalNodeInfluence Whether influence traces the entire DAG paths
 * instead of direct incoming/outgoing edges in normal mode.
 */
const updateEdgeAnimations = (
  isExplaining: boolean,
  isReverseMode: boolean,
  hasDerivativeTarget: boolean,
  selectedNodeIds: string[],
  derivativePathNodeIds: ReadonlySet<string>,
  edges: Edge[],
  isGlobalNodeInfluence: boolean = false,
): Edge[] => {
  const nodeIds = new Set<string>();
  selectedNodeIds.forEach((selectedNodeId) => {
    nodeIds.add(selectedNodeId);
  });

  const { incomingEdgeIds, outgoingEdgeIds } = findInfluenceEdgeIds(
    selectedNodeIds,
    edges,
    isGlobalNodeInfluence,
  );

  return edges.map((edge) => {
    edge.animated = false;
    delete (edge as any).zIndex;
    delete (edge as any).style;

    if (isExplaining && hasDerivativeTarget) {
      if (
        isReverseMode &&
        nodeIds.has(edge.source) &&
        derivativePathNodeIds.has(edge.target)
      ) {
        edge.animated = true;
      } else if (
        !isReverseMode &&
        nodeIds.has(edge.target) &&
        derivativePathNodeIds.has(edge.source)
      ) {
        edge.animated = true;
      }
      // In reverse mode the animated edges leave the selected nodes, so the
      // gradient runs against them and the dashes have to run backwards
      if (edge.animated && isReverseMode) {
        edge.className = reverseFlowClassName;
      } else {
        delete edge.className;
      }
      if (edge.markerEnd !== undefined) {
        edge.markerEnd = buildEdgeMarker();
      }
    } else if (!isExplaining) {
      if (selectedNodeIds.length === 0) {
        delete edge.className;
        if (edge.markerEnd !== undefined) {
          edge.markerEnd = buildEdgeMarker();
        }
      } else {
        const isIncoming = incomingEdgeIds.has(edge.id);
        const isOutgoing = outgoingEdgeIds.has(edge.id);

        if (isOutgoing) {
          edge.animated = true;
          delete edge.className;
          edge.style = { stroke: outgoingInfluenceColor, strokeWidth: 2.5 };
          edge.markerEnd = buildEdgeMarker(outgoingInfluenceColor);
          edge.zIndex = 10;
        } else if (isIncoming) {
          edge.animated = true;
          delete edge.className;
          edge.style = { stroke: incomingInfluenceColor, strokeWidth: 2.5 };
          edge.markerEnd = buildEdgeMarker(incomingInfluenceColor);
          edge.zIndex = 10;
        } else {
          delete edge.className;
          if (edge.markerEnd !== undefined) {
            edge.markerEnd = buildEdgeMarker();
          }
        }
      }
    } else {
      delete edge.className;
      if (edge.markerEnd !== undefined) {
        edge.markerEnd = buildEdgeMarker();
      }
    }
    return edge;
  });
};

/**
 * Direction of the flow highlight relative to the node being evaluated.
 *
 * - `incoming`: the edges feeding the node, i.e., the forward pass and the
 *   forward-mode chain rule
 * - `outgoing`: the edges leaving the node, i.e., the reverse-mode chain rule
 */
type StepFlowDirection = "incoming" | "outgoing";

/**
 * React Flow's default edge stroke. The arrowheads reuse it so that an arrow
 * and the line it sits on always read as one shape.
 */
const edgeMarkerColor = "#b1b1b7";

/**
 * Builds the arrowhead that marks which way an edge points.
 *
 * The graph is a DAG, so every edge has a direction that matters: it decides
 * what feeds what during the forward pass and how the chain rule composes.
 *
 * `markerUnits` is pinned to `userSpaceOnUse` because React Flow's default
 * scales the arrow by the stroke width, which would balloon it while a step is
 * highlighting the edge with a thicker stroke.
 *
 * @param color Arrow color, defaults to the resting edge color.
 * @returns The marker definition to hand to `markerEnd`.
 */
const buildEdgeMarker = (color: string = edgeMarkerColor): EdgeMarker => {
  return {
    type: MarkerType.ArrowClosed,
    color,
    width: 16,
    height: 16,
    markerUnits: "userSpaceOnUse",
  };
};

/**
 * Gives every edge an arrowhead.
 *
 * Needed when loading a graph, since files saved before the arrows existed
 * have no marker on their edges.
 *
 * @param edges React Flow edges.
 * @returns The updated edges.
 */
const addEdgeMarkers = (edges: Edge[]): Edge[] => {
  return edges.map((edge) => {
    return { ...edge, markerEnd: buildEdgeMarker() };
  });
};

/**
 * Updates the label of each edge with its local derivative.
 *
 * @param edges React Flow edges.
 * @param coreGraphAdapter The adapter holding the graph.
 * @param isLocalDerivativesVisible Whether the local derivative setting is on.
 * @param decimalPlaces How many decimals to show, or null.
 * @param isDarkMode Whether dark mode is active.
 * @returns The updated edges.
 */
const updateEdgeLabels = (
  edges: Edge[],
  coreGraphAdapter: CoreGraphAdapter,
  isLocalDerivativesVisible: boolean,
  decimalPlaces: DecimalPlaces,
  isDarkMode: boolean,
): Edge[] => {
  return edges.map((edge) => {
    if (!isLocalDerivativesVisible) {
      if (edge.label === undefined) {
        return edge;
      }
      return {
        ...edge,
        label: undefined,
      };
    }

    const rawDerivative = coreGraphAdapter.getLocalDerivative(
      edge.source,
      edge.target,
    );
    const formattedLabel = formatNumber(rawDerivative, decimalPlaces);

    return {
      ...edge,
      label: formattedLabel,
      labelStyle: {
        fontSize: 11,
        fontWeight: 600,
        fill: isDarkMode ? "#f87171" : "#d32f2f",
      },
      labelShowBg: true,
      labelBgPadding: [3, 6],
      labelBgBorderRadius: 6,
      labelBgStyle: {
        fill: isDarkMode ? "#1e293b" : "#ffffff",
        stroke: isDarkMode
          ? "rgba(248, 113, 113, 0.4)"
          : "rgba(211, 47, 47, 0.3)",
        strokeWidth: 1,
        rx: 6,
        ry: 6,
      },
    };
  });
};

/**
 * Class that flips the direction the dashes travel along an animated edge.
 *
 * Defined in `src/reactflow/ReactFlowGraph.css`. It's needed because React
 * Flow always animates from the source to the target, while reverse-mode
 * differentiation pulls the gradient the other way: from a node's output nodes
 * back into the node.
 */
const reverseFlowClassName = "reverse-flow";

const stepFlowColors: Record<ExecutionStepPhase, string> = {
  value: "#f57c00", // orange[700]
  derivative: "#d81b60", // pink[600]
};

/**
 * Turns the step-by-step execution on or off for every node.
 *
 * Entering step mode hides all the outputs behind a placeholder so that the
 * user can reveal them one at a time. Leaving it shows everything again. The
 * underlying values are never touched, so leaving step mode doesn't need a
 * recomputation.
 *
 * @param isStepMode Whether step mode is on.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const setStepModeOnNodes = (isStepMode: boolean, nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    data.walkthroughState = isStepMode
      ? { status: "waiting" }
      : idleWalkthroughState;
    data.outputItems.forEach((outputItem) => {
      outputItem.isHidden = isStepMode;
    });
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Turns the manual derivative mode on or off for every node.
 *
 * Only the derivative is hidden. The forward pass isn't what the user works
 * out by hand here, and the chain rule is spelled out in terms of the f()
 * values, so hiding those too would hide the very numbers the exercise asks
 * the user to multiply.
 *
 * @param isManualMode Whether the manual derivative mode is on.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const setManualModeOnNodes = (isManualMode: boolean, nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    data.walkthroughState = isManualMode
      ? { status: "waiting" }
      : idleWalkthroughState;
    data.outputItems.forEach((outputItem) => {
      // Every item is set, not just the derivative one, so that the mode holds
      // the same thing back however the canvas got here
      outputItem.isHidden = isManualMode && outputItem.type === "derivative";
    });
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Marks what the manual derivative run has done and what it will accept next.
 *
 * The ready nodes are the interactive part: they're the only ones a click gets
 * anywhere with, so they're the only ones that shouldn't look dimmed.
 *
 * @param activeNodeId Node ID of the node open in the panel, or null when none
 * is. It wins over being ready or done: the ring around the node the user is
 * reading says more than either.
 * @param computedNodeIds Node IDs the user has already worked out.
 * @param readyNodeIds Node IDs the user may work out next.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const updateManualNodeStates = (
  activeNodeId: string | null,
  computedNodeIds: ReadonlySet<string>,
  readyNodeIds: ReadonlySet<string>,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    // Only the chain rule is ever walked by hand, so an active node here is
    // always in the differentiation phase and takes its color
    let walkthroughState: NodeWalkthroughState = { status: "waiting" };
    if (node.id === activeNodeId) {
      walkthroughState = { status: "active", phase: "derivative" };
    } else if (computedNodeIds.has(node.id)) {
      walkthroughState = { status: "done" };
    } else if (readyNodeIds.has(node.id)) {
      walkthroughState = { status: "ready" };
    }
    data.walkthroughState = walkthroughState;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Reveals one output item of one node.
 * @param nodeId Node ID of the node being evaluated.
 * @param outputItemType Which output item to reveal.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const revealStepOutput = (
  nodeId: string,
  outputItemType: OutputItemType,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    if (node.id !== nodeId) {
      return node;
    }

    const data = node.data as NodeData;
    const outputItem = data.outputItems.find(
      (outputItem) => outputItem.type === outputItemType,
    );
    if (outputItem !== undefined) {
      outputItem.isHidden = false;
    }
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Marks which node is being evaluated and which ones are already done.
 * @param activeNodeId Node ID of the node being evaluated, or null when the
 * run hasn't started or has finished.
 * @param activePhase Which phase the active node is being evaluated for.
 * @param visitedNodeIds Node IDs of the nodes that have been evaluated.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const updateReactFlowNodeStepStates = (
  activeNodeId: string | null,
  activePhase: ExecutionStepPhase | null,
  visitedNodeIds: ReadonlySet<string>,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    let walkthroughState: NodeWalkthroughState = { status: "waiting" };
    if (node.id === activeNodeId && activePhase !== null) {
      walkthroughState = { status: "active", phase: activePhase };
    } else if (visitedNodeIds.has(node.id)) {
      walkthroughState = { status: "done" };
    }
    data.walkthroughState = walkthroughState;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Animates the edges that carry the value (or the gradient) into the node
 * being evaluated.
 * @param nodeId Node ID of the node being evaluated, or null to clear the
 * highlight.
 * @param direction Which side of the node the flow comes from.
 * @param phase Which phase the step belongs to, it decides the color.
 * @param flowNodeIds Node IDs the flow may come from, or null to accept any
 * neighbor. The derivative phase restricts it to the differentiation path: a
 * neighbor the target doesn't depend on contributes nothing to the chain rule,
 * so nothing flows along that edge.
 * @param edges React Flow edges.
 * @returns The updated edges.
 */
const highlightStepEdges = (
  nodeId: string | null,
  direction: StepFlowDirection,
  phase: ExecutionStepPhase,
  flowNodeIds: ReadonlySet<string> | null,
  edges: Edge[],
): Edge[] => {
  return edges.map((edge) => {
    const isIncoming = direction === "incoming";
    const isAttached =
      nodeId !== null &&
      (isIncoming ? edge.target === nodeId : edge.source === nodeId);
    // The far end is where the value or the gradient comes from
    const farEndNodeId = isIncoming ? edge.source : edge.target;
    const isFlowing =
      isAttached && (flowNodeIds === null || flowNodeIds.has(farEndNodeId));

    edge.animated = isFlowing;
    edge.zIndex = isFlowing ? 10 : 0;
    edge.style = isFlowing
      ? { stroke: stepFlowColors[phase], strokeWidth: 3 }
      : {};
    edge.markerEnd = buildEdgeMarker(
      isFlowing ? stepFlowColors[phase] : undefined,
    );
    // An outgoing edge means the value travels from the far end back into the
    // node being evaluated, i.e., against the edge, so the dashes must too
    edge.className =
      isFlowing && direction === "outgoing" ? reverseFlowClassName : undefined;
    return edge;
  });
};

/**
 * Removes every step flow highlight.
 * @param edges React Flow edges.
 * @returns The updated edges.
 */
const clearStepEdgeHighlights = (edges: Edge[]): Edge[] => {
  return edges.map((edge) => {
    edge.animated = false;
    edge.zIndex = 0;
    edge.style = {};
    edge.className = undefined;
    edge.markerEnd = buildEdgeMarker();
    return edge;
  });
};

const updateReactFlowNodeFValues = (
  updatedNodeIdToValues: ReadonlyMap<string, string>,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    if (updatedNodeIdToValues.has(node.id)) {
      const value = updatedNodeIdToValues.get(node.id);
      if (value === undefined) {
        throw new Error(`Should find the value of node ${node.id}`);
      }

      const data = node.data as NodeData;
      const outputItem = data.outputItems.find(
        (outputItem) => outputItem.type === "value",
      );
      if (outputItem !== undefined) {
        outputItem.value = value;
        // Set the new data to notify React Flow about the change
        const newData: NodeData = { ...node.data };
        node.data = newData;
      }
    }

    return node;
  });
};

const updateReactFlowNodeDerivatives = (
  updatedNodeIdToDerivatives: ReadonlyMap<string, string>,
  updatedNodeIdToNames: ReadonlyMap<string, string>,
  isReverseMode: boolean,
  derivativeTargetName: string | null,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    if (updatedNodeIdToDerivatives.has(node.id)) {
      const value = updatedNodeIdToDerivatives.get(node.id);
      if (value === undefined) {
        throw new Error(`Should find the value of node ${node.id}`);
      }
      const nodeName = updatedNodeIdToNames.get(node.id);
      if (nodeName === undefined) {
        throw new Error(`Should find the node name of node ${node.id}`);
      }

      const data = node.data as NodeData;
      const outputItem = data.outputItems.find(
        (outputItem) => outputItem.type === "derivative",
      );
      if (outputItem !== undefined) {
        outputItem.labelParts = buildDerivativeLabelParts(
          isReverseMode,
          node.id,
          nodeName,
          derivativeTargetName,
        );
        outputItem.value = value;
        // Set the new data to notify React Flow about the change
        const newData: NodeData = { ...node.data };
        node.data = newData;
      }
    }

    return node;
  });
};

const updateReactFlowNodeDarkMode = (
  isDarkMode: boolean,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    data.isDarkMode = isDarkMode;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Pushes the decimal setting onto every node.
 *
 * The values themselves aren't touched: the nodes format them at render time,
 * so switching the setting reformats the whole graph without recomputing
 * anything.
 *
 * @param decimalPlaces How many decimals the read-only values should show.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const updateReactFlowNodeDecimalPlaces = (
  decimalPlaces: DecimalPlaces,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...data, decimalPlaces };
    node.data = newData;
    return node;
  });
};

/**
 * Updates whether values and derivatives are shown on nodes.
 *
 * @param isNodeValuesVisible Whether operation node values are shown.
 * @param isNodeDerivativesVisible Whether node derivatives are shown.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const updateReactFlowNodeValuesVisibility = (
  isNodeValuesVisible: boolean,
  isNodeDerivativesVisible: boolean,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    const newData: NodeData = {
      ...data,
      isNodeValuesVisible,
      isNodeDerivativesVisible,
    };
    node.data = newData;
    return node;
  });
};

const updateReactFlowNodeHighlighted = (
  derivativeTarget: string | null,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    data.isHighlighted = node.id === derivativeTarget;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

/**
 * Shrinks a node down to its header, or restores it.
 *
 * Only the presentation changes: the core graph keeps every node, edge and
 * value, so collapsing never touches the results. That's also why the
 * collapsed node still has to mount its target handles, otherwise React Flow
 * would have nowhere to land the incoming edges.
 *
 * @param nodeId Node ID of the node to collapse or expand.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const toggleNodeCollapse = (nodeId: string, nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    if (node.id !== nodeId) {
      return node;
    }

    const data = node.data as NodeData;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = {
      ...data,
      isCollapsed: data.isCollapsed !== true,
    };
    node.data = newData;
    return node;
  });
};

/**
 * Collapses or expands every node in one go.
 *
 * It sets the state rather than flipping each node, so a graph with a mix of
 * collapsed and expanded nodes ends up all the same way instead of inverted.
 *
 * @param isCollapsed Whether the nodes should end up collapsed.
 * @param nodes React Flow nodes.
 * @returns The updated nodes.
 */
const setCollapsedOnNodes = (isCollapsed: boolean, nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    // Set the new data to notify React Flow about the change
    const newData: NodeData = { ...data, isCollapsed };
    node.data = newData;
    return node;
  });
};

const findFirstSelectedNodeId = (
  params: OnSelectionChangeParams,
): string | null => {
  const firstNode = params.nodes.find((node) => "id" in node) ?? null;
  if (firstNode === null) {
    return null;
  }
  return firstNode.id;
};

const selectReactFlowNode = (nodeId: string, nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    if (node.id !== nodeId) {
      node.selected = false;
      return node;
    }

    node.selected = true;
    return node;
  });
};

const deselectAllNodes = (nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    node.selected = false;
    return node;
  });
};

const selectAllNodes = (nodes: Node[]): Node[] => {
  return nodes.map((node) => {
    return {
      ...node,
      selected: true,
    };
  });
};

const buildReactFlowNodeData = (addNodeData: AddNodeData): NodeData => {
  const {
    featureNodeType,
    featureOperation,
    nodeId,
    nodeName,
    initialOutputValue,
    isReverseMode,
    derivativeTargetName,
    onNameChange,
    onInputChange,
    onBodyClick,
    onDerivativeClick,
    onToggleCollapse,
    isDarkMode,
    decimalPlaces,
    isNodeValuesVisible,
    isNodeDerivativesVisible,
    topologicalOrder,
    isTopologicalOrderVisible,
  } = addNodeData;
  const isHighlighted = false;

  switch (featureNodeType.nodeType) {
    case "constant": {
      return {
        name: nodeName,
        operationData: null,
        featureNodeType,
        inputItems: [
          {
            id: "value",
            label: "=",
            showHandle: false,
            showInputField: true,
            value: "0",
          },
        ],
        outputItems: [],
        onNameChange,
        onBodyClick,
        onInputChange,
        onDerivativeClick,
        onToggleCollapse,
        isDarkMode,
        decimalPlaces,
        isNodeValuesVisible,
        isNodeDerivativesVisible,
        topologicalOrder,
        isTopologicalOrderVisible,
        isHighlighted,
      };
    }
    case "variable": {
      const derivativeLabelParts = buildDerivativeLabelParts(
        isReverseMode,
        nodeId,
        nodeName,
        derivativeTargetName,
      );
      return {
        name: nodeName,
        operationData: null,
        featureNodeType,
        inputItems: [
          {
            id: "value",
            label: "=",
            showHandle: false,
            showInputField: true,
            value: "0",
          },
        ],
        outputItems: [
          {
            type: "derivative",
            labelParts: derivativeLabelParts,
            value: "0",
          },
        ],
        onNameChange,
        onBodyClick,
        onInputChange,
        onDerivativeClick,
        onToggleCollapse,
        isDarkMode,
        decimalPlaces,
        isNodeValuesVisible,
        isNodeDerivativesVisible,
        topologicalOrder,
        isTopologicalOrderVisible,
        isHighlighted,
      };
    }
    case "operation": {
      const derivativeLabelParts = buildDerivativeLabelParts(
        isReverseMode,
        nodeId,
        nodeName,
        derivativeTargetName,
      );
      if (featureOperation === null) {
        throw new Error("Should provide feature operation");
      }
      return {
        name: nodeName,
        operationData: {
          name: featureOperation.name,
          helpText: featureOperation.helpText,
        },
        featureNodeType,
        inputItems: featureOperation.inputPorts.map((inputPort) => {
          return {
            id: inputPort.getId(),
            label: inputPort.getId(),
            showHandle: true,
            showInputField: true,
            value: "0",
          };
        }),
        outputItems: [
          {
            type: "value",
            labelParts: [
              {
                type: "latex",
                id: "value",
                latex: "=",
              },
            ],
            value: initialOutputValue,
          },
          {
            type: "derivative",
            labelParts: derivativeLabelParts,
            value: "0",
          },
        ],
        onNameChange,
        onBodyClick,
        onInputChange,
        onDerivativeClick,
        onToggleCollapse,
        isDarkMode,
        decimalPlaces,
        isNodeValuesVisible,
        isNodeDerivativesVisible,
        topologicalOrder,
        isTopologicalOrderVisible,
        isHighlighted,
      };
    }
  }
};

const buildDerivativeLabelParts = (
  isReverseMode: boolean,
  nodeId: string,
  nodeName: string,
  derivativeTargetName: string | null,
): LabelType[] => {
  const targetLatex = `\\partial{${derivativeTargetName ?? "?"}}`;
  const nodeLatex = `\\partial{${nodeName}}`;
  const derivativeLatex = isReverseMode
    ? `\\displaystyle \\frac{${targetLatex}}{${nodeLatex}}`
    : `\\displaystyle \\frac{${nodeLatex}}{${targetLatex}}`;
  return [
    {
      type: "latexLink",
      id: "derivative",
      latex: derivativeLatex,
      href: nodeId,
    },
    {
      type: "latex",
      id: "equal",
      latex: "=",
    },
  ];
};

const findLastSelectedNode = (
  nodes: Node[],
  lastSelectedNodeId: string | null,
): Node | null => {
  if (lastSelectedNodeId === null) {
    return null;
  }
  return nodes.find((node) => node.id === lastSelectedNodeId) ?? null;
};

const updateReactFlowNodeTopologicalOrder = (
  topologicalOrderMap: ReadonlyMap<string, number>,
  isTopologicalOrderVisible: boolean,
  nodes: Node[],
): Node[] => {
  return nodes.map((node) => {
    const data = node.data as NodeData;
    data.topologicalOrder = topologicalOrderMap.get(node.id) ?? null;
    data.isTopologicalOrderVisible = isTopologicalOrderVisible;
    const newData: NodeData = { ...node.data };
    node.data = newData;
    return node;
  });
};

export {
  addEdgeMarkers,
  addReactFlowNode,
  buildEdgeMarker,
  clearStepEdgeHighlights,
  deselectAllNodes,
  findFirstSelectedNodeId,
  findRemovedEdges,
  getNewReactFlowNodePosition,
  hideInputField,
  highlightStepEdges,
  revealStepOutput,
  selectAllNodes,
  selectReactFlowNode,
  setCollapsedOnNodes,
  setManualModeOnNodes,
  setStepModeOnNodes,
  showInputFields,
  toggleNodeCollapse,
  updateEdgeAnimations,
  updateEdgeLabels,
  updateManualNodeStates,
  updateReactFlowNodeDarkMode,
  updateReactFlowNodeDecimalPlaces,
  updateReactFlowNodeDerivatives,
  updateReactFlowNodeFValues,
  updateReactFlowNodeHighlighted,
  updateReactFlowNodeInputValue,
  updateReactFlowNodeName,
  updateReactFlowNodeStepStates,
  updateReactFlowNodeTopologicalOrder,
  updateReactFlowNodeValuesVisibility,
};
export type { StepFlowDirection };
